import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getAllTicketStatuses, getPublicTicketStatuses } from '../services/ticketStatusService';
import { useAuth } from './AuthContext';

const TicketStatusContext = createContext();

export const TicketStatusProvider = ({ children }) => {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const location = useLocation();
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchStatuses = async () => {
        const isPublicPage = location.pathname.startsWith('/public');

        // If not authenticated and not on a public page, don't fetch from protected API
        if (!isAuthenticated && !isPublicPage) {
            setStatuses([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            let data;
            if (isAuthenticated) {
                // For internal app, use the authenticated API
                data = await getAllTicketStatuses();
            } else {
                // For public pages, use the public API
                data = await getPublicTicketStatuses();
            }
            setStatuses(Array.isArray(data) ? data : []);
        } catch (error) {
            if (error.response?.status === 401) {
                console.warn('Unauthorized to fetch ticket statuses, user might be logged out.');
                setStatuses([]);
            } else {
                console.error('Failed to fetch ticket statuses:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthLoading) {
            fetchStatuses();
        }
    }, [isAuthenticated, isAuthLoading, location.pathname]);

    const getStatusConfig = (slug) => {
        return statuses.find(s => s.slug === slug) || {
            slug: slug,
            name_en: slug,
            name_th: slug,
            color: '#6c757d',
            icon: 'bi-question-circle'
        };
    };

    const getStatusColor = (slug) => getStatusConfig(slug).color;
    const getStatusLabel = (slug, lang = 'en') => {
        const config = getStatusConfig(slug);
        return lang === 'th' ? config.name_th : config.name_en;
    };
    const getStatusIcon = (slug) => getStatusConfig(slug).icon;

    const getStatusVariant = (slug) => {
        return 'secondary';
    };

    return (
        <TicketStatusContext.Provider value={{
            statuses,
            loading,
            refreshStatuses: fetchStatuses,
            getStatusConfig,
            getStatusColor,
            getStatusLabel,
            getStatusIcon,
            getStatusVariant
        }}>
            {children}
        </TicketStatusContext.Provider>
    );
};

export const useTicketStatuses = () => {
    const context = useContext(TicketStatusContext);
    if (!context) {
        throw new Error('useTicketStatuses must be used within a TicketStatusProvider');
    }
    return context;
};
